import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { LoadingSpinner } from '@kira/checkpoint-shared-frontend';
import React, { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

export interface IDropdownOption {
  label: string;
  icon?: JSX.Element;
  onClick: () => void;
}

interface DropdownProps {
  options: IDropdownOption[];
  loading?: boolean;
  dropdownIcon?: JSX.Element;
  menuClassName?: string;
  optionLabelClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  loading,
  dropdownIcon,
  menuClassName,
  optionLabelClassName,
}) => {
  const menuButtonClassName = (open: boolean) =>
    twMerge(
      'flex',
      'items-center',
      'rounded',
      'transition-all',
      'duration-200',
      'ease-in-out',
      'hover:bg-kira-blue-800',
      'hover:text-kira-slate-0',
      'group/dropdown-button',
      open
        ? 'bg-kira-blue-800 text-kira-slate-50 is-open'
        : 'text-kira-slate-800'
    );

  const menuItemsClassName = twMerge(
    'absolute',
    'right-0',
    'z-10',
    'mt-2',
    'w-fit',
    'origin-top-right',
    'rounded-md',
    'bg-white',
    'shadow-lg',
    'ring-1',
    'ring-black',
    'ring-opacity-5',
    'focus:outline-none',
    menuClassName
  );

  const menuOptionLabelClassName = twMerge(
    'flex',
    'w-fit',
    'items-center',
    'justify-center',
    'gap-1',
    'text-kira-red-800',
    optionLabelClassName
  );

  return (
    <Menu as="div" className="relative inline-block overflow-visible text-left">
      {({ open }) => (
        <>
          <Menu.Button
            className={menuButtonClassName(open)}
            data-testid="options"
          >
            <span className="sr-only">Open options</span>
            {loading ? (
              <LoadingSpinner spinnerClassNames="h-4 w-4 text-slate-500" />
            ) : (
              dropdownIcon || (
                <EllipsisVerticalIcon
                  className="m-1 h-6 w-6"
                  aria-hidden="true"
                />
              )
            )}
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className={menuItemsClassName}>
              <div className="py-1">
                {options.map((option, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        type="button"
                        data-testid={option.label}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                          event.preventDefault();
                          option.onClick();
                        }}
                        className={twMerge(
                          active
                            ? 'bg-gray-100 text-[#333333]'
                            : 'text-gray-700',
                          'block w-full px-4 py-2 text-left text-sm'
                        )}
                      >
                        <div className={menuOptionLabelClassName}>
                          {option.icon}
                          {option.label}
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default Dropdown;
